<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-truck"></i>
        {{ $t("fleetTrucks.page_title") }}
        <span class="countTotal">({{ count }})</span>
        <span class="btn-add">
          <vs-button circle icon flat @click="OpenModalAdd()">
            <i class="fal fa-plus-circle"></i>
          </vs-button>
          <vs-button circle icon flat @click="importOpen = true" :title="$t('fleetTrucks.import_button_title')">
            <i class="fal fa-file-upload"></i>
          </vs-button>
        </span>
      </h4>
    </div>
    <div class="box">
      <div class="columns">
        <div class="column is-4">
          <div class="control">
            <label class="label">{{ $t("fleetTrucks.search_plate_label") }}</label>
            <input class="input" :placeholder="$t('fleetTrucks.search_plate_placeholder')" v-model="searchItem" @keyup.enter="FilterTrucks">
          </div>
        </div>
        <div class="column is-3">
          <div class="control">
            <label class="label">{{ $t("fleetTrucks.size_class") }}</label>
            <div class="select">
              <select class="input" v-model="selectedSizeClass" @change="FilterTrucks">
                <option value="">{{ $t("common.ທັງໝົດ") }}</option>
                <option v-for="(size, index) in sizeClassOptions" :key="index" :value="size.id">
                  {{ size.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="control">
            <label class="label">{{ $t("fleetTrucks.status") }}</label>
            <div class="select">
              <select class="input" v-model="selectedActive" @change="FilterTrucks">
                <option value="">{{ $t("common.ທັງໝົດ") }}</option>
                <option value="1">{{ $t("fleetTrucks.active") }}</option>
                <option value="0">{{ $t("fleetTrucks.inactive") }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="center">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t("fleetTrucks.no") }}</vs-th>
            <vs-th>{{ $t("fleetTrucks.plate_number") }}</vs-th>
            <vs-th>{{ $t("fleetTrucks.vehicle_type") }}</vs-th>
            <vs-th>{{ $t("fleetTrucks.size") }}</vs-th>
            <vs-th>{{ $t("fleetTrucks.capacity_kg_short") }}</vs-th>
            <vs-th>{{ $t("fleetTrucks.capacity_cbm_short") }}</vs-th>
            <vs-th>{{ $t("fleetTrucks.ownership") }}</vs-th>
            <vs-th>{{ $t("fleetTrucks.team") }}</vs-th>
            <vs-th>{{ $t("fleetTrucks.default_crew") }}</vs-th>
            <vs-th>{{ $t("fleetTrucks.status") }}</vs-th>
            <vs-th></vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="index" :data="truck" v-for="(truck, index) in trucks">
            <vs-td :style="{textAlign: 'center'}">{{ index + 1 }}</vs-td>
            <vs-td :style="{color: 'crimson', fontWeight: 'bold'}">{{ truck.plate_number }}</vs-td>
            <vs-td>{{ truck.vehicle_type }}</vs-td>
            <vs-td>{{ FleetSizeClassLabel(truck.size_class) }}</vs-td>
            <vs-td>{{ FleetFormatNumber(truck.capacity_kg) }}</vs-td>
            <vs-td>{{ truck.capacity_cbm }}</vs-td>
            <vs-td>{{ FleetOwnershipLabel(truck.ownership) }}</vs-td>
            <vs-td>{{ truck.team_name || '...' }}</vs-td>
            <vs-td>
              <span v-if="truck.default_drivers && truck.default_drivers.length" class="crew-names">
                {{ truck.default_drivers.map((d) => d.driver_name || ('#' + d.driver_id)).join(', ') }}
              </span>
              <span v-else :style="{color: 'grey'}">...</span>
              <vs-button v-if="truck.default_drivers !== undefined" circle icon flat size="small"
                         @click="OpenCrew(truck)" :title="$t('fleetTrucks.edit_default_crew')">
                <i class="fal fa-user-edit"></i>
              </vs-button>
            </vs-td>
            <vs-td>
              <span :style="{color: truck.is_active ? '#2dcc70' : 'crimson'}">
                {{ truck.is_active ? $t("fleetTrucks.active") : $t("fleetTrucks.inactive") }}
              </span>
            </vs-td>
            <vs-td style="text-align: right; width: 100px">
              <div class="group-icon">
                <vs-button circle icon flat @click="OpenModalEdit(truck.id)">
                  <i class="fad fa-pencil-alt"></i>
                </vs-button>
                <vs-button circle danger icon flat @click="OpenModalDelete(truck.id)">
                  <i class="fad fa-trash-alt"></i>
                </vs-button>
              </div>
            </vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <Pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="offset"
                      @paginate="FetchTrucks()"></Pagination>
          <Loading v-if="isLoading"/>
        </template>
      </vs-table>
    </div>

    <ModalAdd>
      <template v-slot="{ close }">
        <AddTruck @close="close" @success="FetchTrucks()"/>
      </template>
    </ModalAdd>

    <ModalEdit>
      <template v-slot="{ close }">
        <EditTruck :truck="filterTruck" @close="close" @success="FetchTrucks()"/>
      </template>
    </ModalEdit>

    <ModalDelete>
      <template v-slot="{ close }">
        <DeleteTruck :truckId="truckId" @close="close" @success="FetchTrucks()"/>
      </template>
    </ModalDelete>

    <!-- Own dialogs (the store-driven Modal* are one-per-page). v-if re-creates
         the content on every open so no stale file/report survives. -->
    <vs-dialog v-model="importOpen" width="760px">
      <template #header>
        <h4>{{ $t("fleetTrucks.import_dialog_title") }}</h4>
      </template>
      <ImportTrucks v-if="importOpen" @success="FetchTrucks()"/>
    </vs-dialog>

    <vs-dialog v-model="crewOpen" width="600px">
      <template #header>
        <h4>{{ $t("fleetTrucks.default_crew") }}</h4>
      </template>
      <TruckDefaultCrew v-if="crewOpen && crewTruck" :truck="crewTruck"
                        @close="crewOpen = false" @success="FetchTrucks()"/>
    </vs-dialog>
  </div>
</template>

<script>
import Loading from '@views/Loading';
import Pagination from '@/Plugins/Paginations/Pagination';
import MixinFleetTrucks from '@/Mixins/Fleet/Trucks';
import AddTruck from './Dialog/AddTruck';
import EditTruck from './Dialog/EditTruck';
import DeleteTruck from './Dialog/DeleteTruck';
import ImportTrucks from './Dialog/ImportTrucks';
import TruckDefaultCrew from './Dialog/TruckDefaultCrew';

export default {
  components: {
    Loading,
    Pagination,
    AddTruck,
    EditTruck,
    DeleteTruck,
    ImportTrucks,
    TruckDefaultCrew,
  },
  mixins: [MixinFleetTrucks],
  data: () => ({
    importOpen: false,
    crewOpen: false,
    crewTruck: null,
  }),
  watch: {
    searchItem(value) {
      if (value === '') {
        this.FilterTrucks();
      }
    },
  },
  methods: {
    OpenModalAdd() {
      this.$store.commit('modalAdd_State', true);
    },
    OpenModalEdit(truckId) {
      this.filterTruck = {...this.trucks.find((item) => item.id === truckId) || {}};
      this.$store.commit('modalEdit_State', true);
    },
    OpenCrew(truck) {
      this.crewTruck = truck;
      this.crewOpen = true;
    },
    OpenModalDelete(truckId) {
      this.truckId = truckId;
      this.$store.commit('modalDelete_State', true);
    },
  },
  created() {
    this.FetchTrucks();
  },
};
</script>

<style scoped>
.btn-add {
  display: inline-flex;
  gap: 6px;
}

.crew-names {
  font-size: 13px;
}
</style>
